import type { ReactNode } from 'react'
import { X } from 'lucide-react'
import clsx from 'clsx'
import { normalizeEntries } from '@/utils/patchValidation'
import styles from './Patching.module.css'

/** Look up errors for a field path, including nested keys such as `environments.dev.vm_names.0`. */
export type ErrorsFor = (path: string) => string[]

export function FieldErrorList({ messages, id }: { messages: string[]; id?: string }) {
  if (messages.length === 0) return null
  return (
    <ul className={styles.fieldError} id={id} data-testid="field-error">
      {messages.map((m, i) => (
        <li key={`${i}-${m}`}>{m}</li>
      ))}
    </ul>
  )
}

interface FieldProps {
  id: string
  label: string
  required?: boolean
  hint?: string
  errors?: string[]
  children: ReactNode
}

export function Field({ id, label, required, hint, errors = [], children }: FieldProps) {
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </label>
      {children}
      {hint && <span className={styles.hint}>{hint}</span>}
      <FieldErrorList messages={errors} id={`${id}-errors`} />
    </div>
  )
}

interface TagInputProps {
  id: string
  label: string
  values: string[]
  onChange: (values: string[]) => void
  /** Text typed but not yet added. Owned by the form so submit can refuse to drop it silently. */
  draft: string
  onDraftChange: (draft: string) => void
  placeholder?: string
  hint?: string
  errors?: string[]
  upperCase?: boolean
}

/** Multi-entry input: type one or more values (space/comma separated) and press Enter or Add. */
export function TagInput({
  id,
  label,
  values,
  onChange,
  draft,
  onDraftChange,
  placeholder,
  hint,
  errors = [],
  upperCase = false,
}: TagInputProps) {
  function commit() {
    const tokens = draft.split(/[\s,]+/).filter(Boolean)
    if (tokens.length === 0) return
    onChange(normalizeEntries([...values, ...tokens], upperCase))
    onDraftChange('')
  }

  return (
    <Field id={id} label={label} hint={hint} errors={errors}>
      <div className={styles.inlineRow}>
        <input
          id={id}
          className={clsx(styles.input, errors.length > 0 && styles.inputError)}
          value={draft}
          placeholder={placeholder}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commit()
            }
          }}
          aria-describedby={errors.length > 0 ? `${id}-errors` : undefined}
        />
        <button type="button" className={styles.chip} onClick={commit} aria-label={`Add to ${label}`}>
          Add
        </button>
      </div>
      {values.length > 0 && (
        <span className={styles.chips} data-testid={`${id}-values`}>
          {values.map((value) => (
            <span key={value} className={clsx(styles.chip, styles.chipMono)}>
              {value}
              <button
                type="button"
                className={styles.chipRemove}
                aria-label={`Remove ${value} from ${label}`}
                onClick={() => onChange(values.filter((v) => v !== value))}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </span>
      )}
    </Field>
  )
}
